<script setup>
import draggable from 'vuedraggable';
import { v4 as uuid } from 'uuid';
import DashboardRes from './DashboardRes.vue';
import {ref, computed} from "vue";
import {getResourceIcon} from "../../../resources-builder/helpers";

const dashboardResourceOptions = [
    {type: 'dashboardRecentActivity', name: 'Recent activity', description: 'Latest partner conversions'},
    {type: 'dashboardProgramOverview', name: 'Program overview', description: 'Program details for affiliates'},
    {type: 'dashboardCustomBlock', name: 'Universal section', description: 'Combine multiple resources'},
    {type: 'dashboardContentBlock', name: 'Content section', description: 'Image with text and button'},
    {type: 'dashboardTextBlock', name: 'Text', description: 'Formatted rich text content'},
    {type: 'dashboardLinks', name: 'Links', description: 'Curated collection of links'},
    {type: 'dashboardAccordion', name: 'Accordion / FAQ', description: 'Collapsible Q&A sections'},
    {type: 'dashboardCommissionStructure', name: 'Commission structure', description: 'Commission tiers overview'},
    {type: 'dashboardEarningsCalculator', name: 'Earnings calculator', description: 'Interactive earnings preview'},
    {type: 'dashboardEmbed', name: 'Embed code', description: 'Custom HTML or embed code'},
];

const props = defineProps({
    resources: {
        type: Array,
        required: true
    },
    zeroFieldsTitle: String,
    zeroFieldsMessage: String,
    file_upload_url: String,
    file_delete_url: String,
    fetchingResources: {
        type: Boolean,
        default: false
    },
    availablePartnerTags: {
        type: Array,
        default: () => []
    },
})

const emit = defineEmits(['update:resources'])

const drag = ref(false);

const uniqueId = () => {
    return 'a' + uuid().split('-')[0];
}

const resourceOptions = computed(() => ({
    file_upload_url: props.file_upload_url,
    file_delete_url: props.file_delete_url,
    available_partner_tags: props.availablePartnerTags,
}))

// v-model support
const resources = computed({
    get: () => props.resources,
    set: (val) => emit('update:resources', val)
})

const removeResource = (resource) => {
    const updated = props.resources.filter((item) => item !== resource);
    emit('update:resources', updated);
};

const toggleResource = (resource) => {
    const isOpening = !resource.edit;
    if (isOpening) {
        props.resources.forEach(item => {
            if (item !== resource) {
                item.edit = false;
            }
        });
    }
    resource.edit = !resource.edit;
};

const addResource = (type) => {
    if (type === 'dashboardRecentActivity' && dashboardRecentActivityAlreadyIncluded()) {
        return;
    }
    if (type === 'dashboardCommissionStructure' && dashboardCommissionStructureAlreadyIncluded()) {
        return;
    }
    if (type === 'dashboardEarningsCalculator' && dashboardEarningsCalculatorAlreadyIncluded()) {
        return;
    }
    if (type === 'dashboardProgramOverview' && dashboardProgramOverviewAlreadyIncluded()) {
        return;
    }
    // Close all existing resources
    props.resources.forEach(item => {
        item.edit = false;
    });

    const newResource = {
        type,
        edit: type !== 'dashboardRecentActivity',
        title: type === 'dashboardCommissionStructure' ? 'Commission' :
               type === 'dashboardEarningsCalculator' ? 'Potential Earnings' :
               type === 'dashboardProgramOverview' ? 'Program overview' : undefined,
        description: type === 'dashboardCommissionStructure' ? '' :
                     type === 'dashboardEarningsCalculator' ? '' :
                     type === 'dashboardProgramOverview' ? '' : undefined,
        show_tag_based_tiers: type === 'dashboardCommissionStructure' ? false : undefined,
        // Program overview defaults
        product_description: type === 'dashboardProgramOverview' ? '' : undefined,
        product_description_title: type === 'dashboardProgramOverview' ? 'Product overview' : undefined,
        product_description_icon: type === 'dashboardProgramOverview' ? 'box' : undefined,
        ideal_customer: type === 'dashboardProgramOverview' ? '' : undefined,
        ideal_customer_title: type === 'dashboardProgramOverview' ? 'Who it\'s for' : undefined,
        ideal_customer_icon: type === 'dashboardProgramOverview' ? 'users' : undefined,
        selling_points: type === 'dashboardProgramOverview' ? '' : undefined,
        selling_points_title: type === 'dashboardProgramOverview' ? 'Why promote us' : undefined,
        selling_points_icon: type === 'dashboardProgramOverview' ? 'trending-up' : undefined,
        rules: type === 'dashboardProgramOverview' ? '' : undefined,
        rules_title: type === 'dashboardProgramOverview' ? 'Program guidelines' : undefined,
        rules_icon: type === 'dashboardProgramOverview' ? 'shield' : undefined,
        show_commission_summary: type === 'dashboardProgramOverview' ? true : undefined,
        show_cookie_duration: type === 'dashboardProgramOverview' ? true : undefined,
        show_payout_info: type === 'dashboardProgramOverview' ? true : undefined,
        show_additional_tiers: type === 'dashboardProgramOverview' ? true : undefined,
        show_resources_button: type === 'dashboardProgramOverview' ? true : undefined,
        button_label: type === 'dashboardProgramOverview' ? 'Resources' : undefined,
        // Earnings calculator defaults
        pricing_tiers: type === 'dashboardEarningsCalculator' ? [] : undefined,
        slider_min: type === 'dashboardEarningsCalculator' ? 1 : undefined,
        slider_default: type === 'dashboardEarningsCalculator' ? 100 : undefined,
        slider_max: type === 'dashboardEarningsCalculator' ? 500 : undefined,
        show_earnings_table: type === 'dashboardEarningsCalculator' ? true : undefined,
        // Custom block defaults
        items: type === 'dashboardCustomBlock' ? [] : undefined,
        unique_id: uniqueId()
    };

    emit('update:resources', [...props.resources, newResource]);
};

const dashboardRecentActivityAlreadyIncluded = () => {
    return resources.value?.some(resource => resource.type === 'dashboardRecentActivity')
}

const dashboardCommissionStructureAlreadyIncluded = () => {
    return resources.value?.some(resource => resource.type === 'dashboardCommissionStructure')
}

const dashboardEarningsCalculatorAlreadyIncluded = () => {
    return resources.value?.some(resource => resource.type === 'dashboardEarningsCalculator')
}

const dashboardProgramOverviewAlreadyIncluded = () => {
    return resources.value?.some(resource => resource.type === 'dashboardProgramOverview')
}
</script>

<template>
    <div>
        <div>
            <!-- Static content - always visible block -->
            <div class="bg-white rounded border mb-2">
                <div class="flex justify-between px-2 py-3 handle">
                    <div class="flex items-center space-x-3 truncate">
                        <div class="focus:outline-none">
                            <svg
                                class="w-5 h-5 text-gray-300"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
                            </svg>
                        </div>
                        <svg class="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                        </svg>
                        <span class="text-gray-500 text-sm truncate">Link sharing and affiliate data</span>
                    </div>
                </div>
            </div>

            <!-- Slot for loading placeholder -->
            <slot name="loading" />

            <!-- Draggable dashboard resources -->
            <draggable
                class="space-y-2 overflow-auto"
                v-model="resources"
                handle=".handle"
                group="dashboardResources"
                @start="drag = true"
                direction="vertical"
                chosen-class="bg-gray-100"
                @end="drag = false"
                item-key="$index"
            >
                <template #item="{ element }">
                    <DashboardRes
                        :data="element"
                        :options="resourceOptions"
                        :unique_id="element.unique_id ?? uniqueId()"
                        @remove="removeResource"
                        @toggle="toggleResource"
                    />
                </template>
            </draggable>

            <div v-if="fetchingResources"
                 class="border rounded p-4 w-full">
                <div class="animate-pulse flex flex-col space-y-4">
                    <div class="border border-gray-200 flex p-3 rounded space-x-4 w-full">
                        <div class="rounded bg-gray-200 h-7 w-7"></div>
                        <div class="flex-1 space-y-6 py-1">
                            <div class="space-y-3">
                                <div class="grid grid-cols-3 gap-4">
                                    <div class="h-2 bg-gray-200 rounded col-span-2"></div>
                                    <div class="h-2 bg-gray-200 rounded col-span-1"></div>
                                </div>
                                <div class="h-2 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                    <div class="border border-gray-200 flex p-3 rounded space-x-4 w-full">
                        <div class="rounded bg-gray-200 h-7 w-7"></div>
                        <div class="flex-1 space-y-6 py-1">
                            <div class="space-y-3">
                                <div class="grid grid-cols-3 gap-4">
                                    <div class="h-2 bg-gray-200 rounded col-span-2"></div>
                                    <div class="h-2 bg-gray-200 rounded col-span-1"></div>
                                </div>
                                <div class="h-2 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                    <div class="border border-gray-200 flex p-3 rounded space-x-4 w-full">
                        <div class="rounded bg-gray-200 h-7 w-7"></div>
                        <div class="flex-1 space-y-6 py-1">
                            <div class="space-y-3">
                                <div class="grid grid-cols-3 gap-4">
                                    <div class="h-2 bg-gray-200 rounded col-span-2"></div>
                                    <div class="h-2 bg-gray-200 rounded col-span-1"></div>
                                </div>
                                <div class="h-2 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty state -->
            <div v-if="!resources?.length && !fetchingResources" class="rounded border">
                <div class="flex justify-center items-center text-center p-20">
                    <div>
                        <div class="flex justify-center items-center p-4">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                <path fill="none" d="M0 0h24v24H0z"/>
                                <path d="M21 4H7a2 2 0 1 0 0 4h14v13a1 1 0 0 1-1 1H7a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4h13a1 1 0 0 1 1 1v1zM5 18a2 2 0 0 0 2 2h12V10H7a3.982 3.982 0 0 1-2-.535V18zM20 7H7a1 1 0 1 1 0-2h13v2z"/>
                            </svg>
                        </div>
                        <h2 class="font-semibold text-lg mb-2">{{ zeroFieldsTitle }}</h2>
                        <p class="text-gray-500 text-sm">{{ zeroFieldsMessage }}</p>
                    </div>
                </div>
            </div>

            <div class="w-max mt-2">
                <options :auto-position="true" :is-right="false" width="!max-w-none !whitespace-normal w-[210px] sm:w-[380px] max-w-[calc(100vw-2rem)]">
                    <template #toggle>
                        <btn type="secondary" icon-name="more" icon-right="true" v-on:click.prevent>Add resource</btn>
                    </template>
                    <template v-slot:items="{ setShow }">
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-0.5 p-1.5">
                            <button
                                v-for="resource in dashboardResourceOptions"
                                :key="resource.type"
                                type="button"
                                class="flex items-start gap-2.5 px-3 py-2 text-left rounded hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-800"
                                @click="addResource(resource.type);setShow(false)"
                            >
                                <svg class="w-5 h-5 shrink-0 mt-0.5 text-gray-500 hidden" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-html="getResourceIcon(resource.type)"></svg>
                                <span>
                                    <span class="block text-sm font-medium text-gray-900 dark:text-gray-300">{{ resource.name }}</span>
                                    <span class="block text-xs text-gray-400 leading-tight">{{ resource.description }}</span>
                                </span>
                            </button>
                        </div>
                    </template>
                </options>
            </div>
        </div>
    </div>
</template>

